import { ROLE_COLORS, RECENT_SLOTS_KEY } from './constants';

// Get display color for a user: personal color first, then role color, then fallback
export function getUserColor(user?: { color?: string | null; role?: string } | null, fallback = '#6b7280'): string {
  if (user?.color) return user.color;
  if (user?.role && ROLE_COLORS[user.role]) return ROLE_COLORS[user.role];
  return fallback;
}

// Helper to format 24-hour time to 12-hour with AM/PM
export function formatTime12Hour(time: string): string {
  if (!time || !time.includes(':')) return time || '';
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Helper to compute remaining availability after claimed slots
export function computeRemainingAvailability(
  windows: { start_time: string; end_time: string }[],
  claimedSlots: { start_time: string; end_time: string; status: string }[]
): { start: string; end: string }[] {
  // Filter out cancelled slots
  const activeSlots = claimedSlots.filter((s) => s.status !== 'cancelled');

  // Convert times to minutes for easier calculation
  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };
  const minutesToTime = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const remaining: { start: string; end: string }[] = [];

  for (const window of windows) {
    const windowStart = timeToMinutes(window.start_time);
    const windowEnd = timeToMinutes(window.end_time);

    // Get all slots that overlap with this window, sorted by start time
    const overlappingSlots = activeSlots
      .filter((s) => {
        const slotStart = timeToMinutes(s.start_time);
        const slotEnd = timeToMinutes(s.end_time);
        return slotStart < windowEnd && slotEnd > windowStart;
      })
      .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

    let currentStart = windowStart;

    for (const slot of overlappingSlots) {
      const slotStart = Math.max(timeToMinutes(slot.start_time), windowStart);
      const slotEnd = Math.min(timeToMinutes(slot.end_time), windowEnd);

      // If there's a gap before this slot, add it as remaining
      if (slotStart > currentStart) {
        remaining.push({
          start: minutesToTime(currentStart),
          end: minutesToTime(slotStart),
        });
      }
      currentStart = Math.max(currentStart, slotEnd);
    }

    // If there's remaining time after all slots
    if (currentStart < windowEnd) {
      remaining.push({
        start: minutesToTime(currentStart),
        end: minutesToTime(windowEnd),
      });
    }
  }

  return remaining;
}

export function getRecentSlots(): { start: string; end: string }[] {
  try {
    const stored = localStorage.getItem(RECENT_SLOTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveRecentSlot(start: string, end: string) {
  const slots = getRecentSlots();
  // Remove duplicates
  const filtered = slots.filter(s => s.start !== start || s.end !== end);
  // Add to front
  const updated = [{ start, end }, ...filtered].slice(0, 5); // Keep last 5
  localStorage.setItem(RECENT_SLOTS_KEY, JSON.stringify(updated));
}

export function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'parent': return 'bg-purple-500/20 text-purple-400';
    case 'caretaker': return 'bg-blue-500/20 text-blue-400';
    case 'family': return 'bg-amber-500/20 text-amber-400';
    case 'professional': return 'bg-teal-500/20 text-teal-400';
    default: return 'bg-gray-500/20 text-gray-400';
  }
}

export function getDisplayRole(role: string): string {
  switch (role) {
    case 'parent': return 'Guardian';
    case 'caretaker': return 'Caretaker';
    case 'family': return 'Family';
    case 'professional': return 'Professional';
    default: return role.charAt(0).toUpperCase() + role.slice(1);
  }
}

export function getInitials(user: { first_name: string; last_name?: string | null }): string {
  return `${user.first_name.charAt(0)}${user.last_name?.charAt(0) || ''}`.toUpperCase();
}

// Calculate hours from time strings
export function calculateHours(startTime: string, endTime: string): number {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  return (endH * 60 + endM - startH * 60 - startM) / 60;
}
